import './booking.scss';

import { Card } from '@transferz/fe-component-library/src/components/card/Card';
import { ContentHeader } from '@transferz/fe-component-library/src/components/contentHeader/ContentHeader';
import Calendar from '@transferz/fe-component-library/src/components/icons/CalendarIcon';
import Clock from '@transferz/fe-component-library/src/components/icons/ClockIcon';
import Plane from '@transferz/fe-component-library/src/components/icons/PlaneIcon';
import Ship from '@transferz/fe-component-library/src/components/icons/ShipIcon';
import Train from '@transferz/fe-component-library/src/components/icons/TrainIcon';
import { PickupTimelineNotification, PickupTimelineNotificationProps } from '@transferz/fe-component-library/src/components/pickupTimeline/PickupTimelineNotification';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import { NotificationVariants } from '@transferz/fe-component-library/src/types/notification';
import { IJourney, IUnresolvedAlert, JourneyStatusType, UnresolvedAlertType } from '@transferz/fe-models';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocalize } from 'localize-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';

import { Loading } from '../../components/loading/Loading';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useRenderLocalizedTemplate } from '../../hooks/useRenderLocalizedTemplate';
import { useMutationResolveAlert } from '../../queries/booking/hooks/useMutationResolveAlert';
import { useQueryJourneyChangesByJourneyId } from '../../queries/booking/hooks/useQueryJourneyChangesByJourneyId';
import { useQueryJourneysByBookingCode } from '../../queries/booking/hooks/useQueryJourneysByBookingCode';
import { useQueryUnresolvedAlerts } from '../../queries/booking/hooks/useQueryUnresolvedAlerts';
import { isJourneyEditable } from '../../utils/journeyUtils';
import { formatDate, formatTime, getTimeZoneShortNameAndOffset } from '../../utils/utils';
import { BookingStatus } from '../journey/components/bookingStatus/BookingStatus';
import { LocationIcon } from '../journey/components/locationIcon/LocationIcon';
import FlightTrackingLabel from './components/flightTrackingLabel/FlightTrackingLabel';
import { UnresolvedAlerts } from './components/UnresolvedAlerts';

interface IUnresolvedPhoneAlert extends IUnresolvedAlert {
  type: UnresolvedAlertType.IncorrectTravellerPhone;
}

interface IUnresolvedFlightAlert extends IUnresolvedAlert {
  type: UnresolvedAlertType.FlightNumberNotFound | UnresolvedAlertType.FlightNumberIncorrectAirport |
    UnresolvedAlertType.FlightNumberNotProvided | UnresolvedAlertType.IncorrectFlightNumber;
}

const isFlightAlert = (alert: IUnresolvedAlert): alert is IUnresolvedFlightAlert => {
  return alert.type === UnresolvedAlertType.FlightNumberNotFound
    || alert.type === UnresolvedAlertType.FlightNumberIncorrectAirport
    || alert.type === UnresolvedAlertType.FlightNumberNotProvided
    || alert.type === UnresolvedAlertType.IncorrectFlightNumber;
};

const isPhoneAlert = (alert: IUnresolvedAlert): alert is IUnresolvedPhoneAlert => {
  return alert.type === UnresolvedAlertType.IncorrectTravellerPhone;
};

const Booking: React.FC<any> = ({ params: { bookingCode } }) => {
  const flags = useFlags();
  const { translate } = useLocalize();
  const [location, setLocation] = useLocation();
  const { renderLocalizedTemplate } = useRenderLocalizedTemplate();
  const [, , removeValue] = useLocalStorage('initAlertResolutionNotification');
  const [unresolvedPhoneAlerts, setUnresolvedPhoneAlerts] = useState<IUnresolvedPhoneAlert[]>([]);
  const [unresolvedFlightAlerts, setUnresolvedFlightAlerts] = useState<IUnresolvedFlightAlert[]>([]);
  const [statuses, setStatuses] = useState<any>({});
  const [changesLoading, setChangesLoading] = useState(true);
  const [showAlertResolveNotification, setShowAlertResolveNotification] = useState(false);
  const [alertResolveNotificationMessage, setAlertResolveNotificationMessage] = useState('' as any);
  const [alertResolvenotificationVariant, setAlertResolvenotificationVariant] = useState('' as any);
  const urlParams: any = new URLSearchParams(window.location.search);
  const phoneNumberVerified = urlParams.get('phone-number-verified');
  const token = urlParams.get('token');
  const { data: journeysData, loading: journeysLoading } = useQueryJourneysByBookingCode({ variables: { bookingCode, includePickupTimeline: !!flags?.transferTimelineEnabled } });
  const { refetch: getJourneyChangeByJourneyId } = useQueryJourneyChangesByJourneyId({ variables: { id: 0, skip: true } });
  const { refetch: getUnresolvedAlerts } = useQueryUnresolvedAlerts({
    variables: {
      params: {
        bookingId: 1,
      }, skip: true,
    },
  });
  const journeysList = journeysData?.journeysByBookingCode?.results?.slice().sort((a: any, b: any) => a.journeyExecutionInfo?.pickupDate > b.journeyExecutionInfo?.pickupDate ? 1 : -1);
  const [resolveAlert] = useMutationResolveAlert();

  const actions: Record<string, (journey: any) => void> = {
    INSUFFICIENT_TIME_BETWEEN_FLIGHT_ARRIVAL_AND_PICKUP: (journey: any) => {
      setLocation(`${location}/journey/${journey?.id}/edit`);
    },
  };
  const handleGetUnresolvedAlerts = () => {
    setUnresolvedPhoneAlerts([]);
    getUnresolvedAlerts({
      params: {
        bookingId: journeysList[0]?.bookingId,
      },
      skip: false,
    })
      .then(({ data }: any) => {
        const unresolvedPhoneAlertsData: IUnresolvedPhoneAlert[] = [];
        const incorrectPhoneAlert: IUnresolvedPhoneAlert = data?.unresolvedAlerts.find((alert: IUnresolvedAlert) => isPhoneAlert(alert));
        if (incorrectPhoneAlert) {
          unresolvedPhoneAlertsData.push({ ...incorrectPhoneAlert, phone: journeysList[0]?.travellerInfo?.phone });
        }
        setUnresolvedPhoneAlerts(unresolvedPhoneAlertsData);

        const unresolvedFlightAlertsData: IUnresolvedFlightAlert[] = [];
        const flightNumberAlerts: IUnresolvedFlightAlert[] = data?.unresolvedAlerts.filter((alert: IUnresolvedAlert) => isFlightAlert(alert));

        if (flightNumberAlerts.length && flags?.showFlightNumberAlerts) {
          flightNumberAlerts.forEach((flightNumberAlert) => {
            const journeyId = flightNumberAlert.journeyIds[0];
            const journey = journeysList.find((journey: IJourney) => journey.id === journeyId);
            if (flags?.enableJourneyModification && isJourneyEditable(journey)) {
              const hubIataCode = journey?.hub?.details.filter((detail: any) => (detail.code === 'iata'))[0]?.value;
              const extendedFlightNumberAlert = {
                ...flightNumberAlert,
                journeyCode: journey.journeyCode,
                journeyId: journey.id,
                flightNumber: journey.travellerInfo.flightNumber,
                travellerInfo: journey.travellerInfo,
                pickupDate: journey?.journeyExecutionInfo?.pickupDate,
                isJourneyInbound: journey?.originLocation?.hub,
                hubIataCode,
              };
              unresolvedFlightAlertsData.push(extendedFlightNumberAlert);
            }
          });
          setUnresolvedFlightAlerts(unresolvedFlightAlertsData);
        }
      });
  };
  useEffect(() => {
    if (!journeysLoading) {
      let statuses: any = {};
      if (flags?.showUnresolvedAlerts && !phoneNumberVerified && !token) {
        handleGetUnresolvedAlerts();
      }
      if (phoneNumberVerified && token) {
        resolveAlert({
          variables: {
            token,
          },
        })
          .then(() => {
            setAlertResolvenotificationVariant(NotificationVariants.success);
            setAlertResolveNotificationMessage(translate('h_successfully_verified_phone'));
          })
          .catch(() => {
            setAlertResolvenotificationVariant(NotificationVariants.error);
            setAlertResolveNotificationMessage(translate('booking_somethingWentWrong'));
            handleGetUnresolvedAlerts();
          })
          .finally(() => {
            window.history.replaceState({}, document.title, window.location.pathname);
            removeValue('initAlertResolutionNotification');
            setShowAlertResolveNotification(true);
          });
      }
      for (const journey of journeysList) {
        getJourneyChangeByJourneyId({ id: journey.id, skip: false })
          .then(({ data }: any) => {
            const valueChanges = data?.journeyChangesByJourneyId?.map((change: any) => change.valueChanges);
            let driverUnderwayChanges: any = [];
            valueChanges?.map((valueChange: any) => {
              return valueChange?.find((change: any) => {
                if (change?.fieldName === 'assignedDriverId' && change?.newValue === null && journey.status === JourneyStatusType.Confirmed) {
                  driverUnderwayChanges.push(change);
                  return;
                }
              });
            });
            if (driverUnderwayChanges?.length >= 1) {
              statuses[journey.id] = { status: JourneyStatusType.DriverUnderway };
            } else {
              statuses[journey.id] = { status: journey.status };
            }
            if (Object.keys(statuses).length === journeysList.length) {
              setStatuses(statuses);
              setChangesLoading(false);
            }
          });
      }
    }
  }, [journeysData]);

  return (
    <div className="booking">
      {
        <ContentHeader
          title={translate('yourBooking')}
          bookingCode={bookingCode}
        />
      }
      <div className="booking-content">
        {(journeysLoading || changesLoading) && !statuses.length
          ? <Loading />
          : (
            <>
              <UnresolvedAlerts
                unresolvedAlerts={unresolvedPhoneAlerts}
                showAlertResolveNotification={showAlertResolveNotification}
                alertResolveNotificationMessage={alertResolveNotificationMessage}
                alertResolvenotificationVariant={alertResolvenotificationVariant}
                refetchUnresolvedAlerts={handleGetUnresolvedAlerts}
              />
              {!!journeysList?.length && journeysList?.map((journey: any, index: number) => {
                return (
                  <div className="card-wrap" key={journey?.journeyCode}>
                    <Card
                      title={`${translate('Journey')} ${journey?.journeyCode}`}
                      titleColor="blue5"
                      chevronDirection="right"
                      collapsibleIcon={false}
                      collapsed={false}
                      onClick={() => setLocation(`/booking/${bookingCode}/journey/${journey.id}`)}
                    >
                      <BookingStatus
                        journey={journey}
                        statuses={statuses}
                      />
                      <div className="trip">
                        <div className="trip-address">
                          <div data-testid="info_address-from" className="pb-32 trip-info-item info-first">
                            <LocationIcon location={journey?.originLocation} inbound={!(journey?.originLocation?.hubData?.hubType === 'AIRPORT')} />
                            <Typography variant="body1">
                              {journey?.originLocation?.hub ? journey?.originLocation?.hubData.autoCompleteName : journey?.originLocation?.address?.originalAddress}
                            </Typography>
                            {!journey?.originLocation?.hub && <Typography variant="body1" color="asphalt5">{journey?.originLocation?.address?.formattedAddress}</Typography>}
                          </div>
                          <div data-testid="info_address-to" className="trip-info-item">
                            <LocationIcon location={journey?.destinationLocation} inbound={!(journey?.destinationLocation?.hubData?.hubType !== 'AIRPORT')} />
                            <Typography variant="body1">
                              {journey?.destinationLocation?.hub ? journey?.destinationLocation?.hubData.autoCompleteName : journey?.destinationLocation?.address?.originalAddress}
                            </Typography>
                            {!journey?.destinationLocation?.hub && <Typography variant="body2" color="asphalt5">{journey?.destinationLocation?.address?.formattedAddress}</Typography>}
                          </div>
                        </div>
                        <div className="trip-date">
                          <div data-testid="info_pickup-date" className="trip-date-info">
                            <Calendar className="icon-calendar" />
                            <Typography variant="body1">{formatDate(journey?.journeyExecutionInfo?.pickupDate, 'DD-MM-YYYY')}</Typography>
                          </div>
                          <div data-testid="info_pickup-time" className="trip-date-info">
                            <Clock className="icon-calendar" direction="right" />
                            <div>
                              <div className="time-container">
                                <Typography variant="body1">{formatTime(journey?.journeyExecutionInfo?.pickupDate, 'HH:mm')}</Typography>
                                {flags?.transferTimelineEnabled && journey?.originLocation?.hub && journey?.journeyExecutionInfo?.pickupTimeAdjustmentMethod === 'FLIGHT_TRACKING' && < FlightTrackingLabel />}
                              </div>
                              <div className="local-time">{`${translate('booking_localTime')} ${getTimeZoneShortNameAndOffset(journey?.journeyExecutionInfo?.pickupTimeZone)}`} </div>
                            </div>
                          </div>
                        </div>
                        {journey?.travellerInfo?.flightNumber && <div className="flight-ship-train-number">
                          <Plane className="icon-calendar mt-2" direction="right" />
                          <Typography variant="body1">{journey?.travellerInfo?.flightNumber}</Typography>
                        </div>}
                        {journey?.travellerInfo?.shipName && <div className="flight-ship-train-number">
                          <Ship className="icon-calendar mt-2" direction="right" />
                          <Typography variant="body1">{journey?.travellerInfo?.shipName}</Typography>
                        </div>}
                        {journey?.travellerInfo?.trainNumber && <div className="flight-ship-train-number">
                          <Train className="icon-calendar mt-2" direction="right" />
                          <Typography variant="body1">{journey?.travellerInfo?.trainNumber}</Typography>
                        </div>}
                        {journey?.pickupTimeline && journey?.pickupTimeline.notifications[0] && <div key={journey?.pickupTimeline.notifications.type} className="notification-container">
                          <PickupTimelineNotification
                            iconUrl={translate(
                              `o_pickup_timeline_notification_${journey?.pickupTimeline.notifications[0].type}.0.icon.filename`,
                            )}
                            title={translate(
                              `o_pickup_timeline_notification_${journey?.pickupTimeline.notifications[0].type}.0.title`,
                            )}
                            description={renderLocalizedTemplate(
                              `o_pickup_timeline_notification_${journey?.pickupTimeline.notifications[0].type}.0.description`, { pickupTimeline: journey?.pickupTimeline },
                            )}
                            variant={journey?.pickupTimeline?.notifications[0].severity?.toLowerCase() as PickupTimelineNotificationProps['variant']}
                            action={journey?.pickupTimeline.notifications[0].type && actions[journey?.pickupTimeline.notifications[0].type] ? {
                              label: translate(`o_pickup_timeline_notification_${journey?.pickupTimeline.notifications[0].type}.0.action_label`),
                              iconUrl: translate(`o_pickup_timeline_notification_${journey?.pickupTimeline.notifications[0].type}.0.action_icon.filename`),
                              onClick: () => actions[journey?.pickupTimeline.notifications[0].type](journey),
                            } : undefined}
                          />
                        </div>}
                      </div>

                      <div className="journey-unresolved-alerts">
                        <UnresolvedAlerts
                          unresolvedAlerts={unresolvedFlightAlerts.filter(alert => alert.journeyIds?.includes(journey.id))}
                          showAlertResolveNotification={false}
                          alertResolveNotificationMessage={alertResolveNotificationMessage}
                          alertResolvenotificationVariant={alertResolvenotificationVariant}
                          refetchUnresolvedAlerts={handleGetUnresolvedAlerts}
                        />
                      </div>
                    </Card>
                  </div>
                );
              })}
            </>
          )
        }
      </div>
    </div>
  );
};

export default Booking;
